// apps/web/src/pages/Auth/VerifyEmail.tsx
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { authApi } from '@/lib/api';
import { Button, Logo } from '@/components/common';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'no-token'>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    const verifyEmail = async () => {
      try {
        const result = await authApi.verifyEmail(token);
        if (result.success) {
          setStatus('success');
          setMessage(result.message);
        } else {
          setStatus('error');
          setMessage('Verification failed');
        }
      } catch (err: any) {
        setStatus('error');
        setMessage(err.message || 'Failed to verify email');
      }
    };

    verifyEmail();
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setResendLoading(true);
    try {
      await authApi.resendVerification(email);
      setResendSuccess(true);
    } catch (err) {
      // Still show success to prevent email enumeration
      setResendSuccess(true);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <Logo size="lg" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-16 h-16 text-coral-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Verifying your email...
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                Please wait while we verify your email address.
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Email Verified!
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {message || 'Your email has been verified successfully. You can now access all features.'}
              </p>
              <Link to="/login">
                <Button className="mt-6 bg-coral-500 hover:bg-coral-600 text-white px-6 py-2.5 rounded-lg">
                  Continue to Login
                </Button>
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Verification Failed
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {message || 'The verification link is invalid or has expired.'}
              </p>

              {/* Resend verification form */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Need a new verification link?
                </p>
                {resendSuccess ? (
                  <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm">
                    If this email is registered, a new verification link has been sent.
                  </div>
                ) : (
                  <form onSubmit={handleResend} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={resendLoading}
                      className="w-full bg-coral-500 hover:bg-coral-600 text-white py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
                    >
                      {resendLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Resend Verification Email'
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          )}

          {status === 'no-token' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-10 h-10 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Check Your Email
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                We sent you a verification link. Click the link in your email to verify your account.
              </p>

              {/* Resend verification form */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Didn't receive the email?
                </p>
                {resendSuccess ? (
                  <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg text-sm">
                    If this email is registered, a new verification link has been sent.
                  </div>
                ) : (
                  <form onSubmit={handleResend} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={resendLoading}
                      className="w-full bg-coral-500 hover:bg-coral-600 text-white py-2.5 rounded-lg text-sm flex items-center justify-center gap-2"
                    >
                      {resendLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Resend Verification Email'
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* Back to login link */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-coral-600 hover:text-coral-700 dark:text-coral-400"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
